import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import {
  Container,
  Title,
  Button,
  Table,
  Modal,
  TextInput,
  Select,
  PasswordInput,
  Stack,
  Group,
  ActionIcon,
  Badge,
  Alert,
  Loader,
} from '@mantine/core';
import { IconPlus, IconEdit, IconTrash, IconAlertCircle } from '@tabler/icons-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { userApi, User, CreateUserDto, UpdateUserDto } from '../../../api/users';
import { useAuth } from '../../../auth/AuthContext';
import dayjs from 'dayjs';

const ROLES = [
  { value: 'ADMIN', label: 'Admin' },
  { value: 'PROCUREMENT_OFFICER', label: 'Procurement Officer' },
  { value: 'HEAD_OF_PROCUREMENT', label: 'Head of Procurement' },
  { value: 'BID_COMMITTEE_MEMBER', label: 'Bid Committee Member' },
  { value: 'CEO', label: 'CEO' },
  { value: 'VIEWER', label: 'Viewer' },
];

export function UserManagement() {
  const { user: currentUser, loading: authLoading } = useAuth();
  const queryClient = useQueryClient();
  const [createModalOpened, setCreateModalOpened] = useState(false);
  const [editModalOpened, setEditModalOpened] = useState(false);
  const [deleteModalOpened, setDeleteModalOpened] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [formData, setFormData] = useState<CreateUserDto>({
    fullName: '',
    email: '',
    password: '',
    role: 'PROCUREMENT_OFFICER',
  });

  // Fetch users
  const { data: users, isLoading } = useQuery({
    queryKey: ['users'],
    queryFn: () => userApi.listUsers(),
  });

  // Create user mutation
  const createUser = useMutation({
    mutationFn: (data: CreateUserDto) => userApi.createUser(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      notifications.show({
        title: 'Success',
        message: 'User created successfully',
        color: 'green',
      });
      setCreateModalOpened(false);
      setFormData({
        fullName: '',
        email: '',
        password: '',
        role: 'PROCUREMENT_OFFICER',
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Error',
        message: error.response?.data?.error || 'Failed to create user',
        color: 'red',
      });
    },
  });

  // Update user mutation
  const updateUser = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateUserDto }) =>
      userApi.updateUser(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      notifications.show({
        title: 'Success',
        message: 'User updated successfully',
        color: 'green',
      });
      setEditModalOpened(false);
      setSelectedUser(null);
      setFormData({
        fullName: '',
        email: '',
        password: '',
        role: 'PROCUREMENT_OFFICER',
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Error',
        message: error.response?.data?.error || 'Failed to update user',
        color: 'red',
      });
    },
  });

  // Delete user mutation
  const deleteUser = useMutation({
    mutationFn: (id: string) => userApi.deleteUser(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      notifications.show({
        title: 'Success',
        message: 'User deleted successfully',
        color: 'green',
      });
      setDeleteModalOpened(false);
      setSelectedUser(null);
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Error',
        message: error.response?.data?.error || 'Failed to delete user',
        color: 'red',
      });
    },
  });

  const handleCreate = () => {
    createUser.mutate(formData);
  };

  const handleEdit = () => {
    if (!selectedUser) return;
    const updateData: UpdateUserDto = {
      fullName: formData.fullName,
      email: formData.email,
      role: formData.role,
    };
    if (formData.password) {
      updateData.password = formData.password;
    }
    updateUser.mutate({ id: selectedUser.id, data: updateData });
  };

  const handleDelete = () => {
    if (!selectedUser) return;
    deleteUser.mutate(selectedUser.id);
  };

  const openEditModal = (user: User) => {
    setSelectedUser(user);
    setFormData({
      fullName: user.fullName,
      email: user.email,
      password: '',
      role: user.role,
    });
    setEditModalOpened(true);
  };

  const openDeleteModal = (user: User) => {
    setSelectedUser(user);
    setDeleteModalOpened(true);
  };

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'ADMIN':
        return 'red';
      case 'PROCUREMENT_OFFICER':
        return 'blue';
      case 'HEAD_OF_PROCUREMENT':
        return 'cyan';
      case 'BID_COMMITTEE_MEMBER':
        return 'purple';
      case 'CEO':
        return 'orange';
      case 'VIEWER':
        return 'gray';
      default:
        return 'gray';
    }
  };

  if (authLoading) {
    return (
      <Container size="xl" py="xl">
        <Loader size="lg" />
      </Container>
    );
  }

  if (currentUser?.role !== 'ADMIN') {
    return <Navigate to="/dashboard" replace />;
  }

  if (isLoading) {
    return (
      <Container size="xl" py="xl">
        <Loader size="lg" />
      </Container>
    );
  }

  return (
    <Container size="xl" py="xl">
      <Group justify="space-between" mb="xl">
        <Title order={2}>User Management</Title>
        <Button
          leftSection={<IconPlus size={16} />}
          onClick={() => {
            setFormData({
              fullName: '',
              email: '',
              password: '',
              role: 'PROCUREMENT_OFFICER',
            });
            setCreateModalOpened(true);
          }}
        >
          Add User
        </Button>
      </Group>

      <Table striped highlightOnHover>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Full Name</Table.Th>
            <Table.Th>Email</Table.Th>
            <Table.Th>Role</Table.Th>
            <Table.Th>Created At</Table.Th>
            <Table.Th>Actions</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {users?.map((user) => (
            <Table.Tr key={user.id}>
              <Table.Td>{user.fullName}</Table.Td>
              <Table.Td>{user.email}</Table.Td>
              <Table.Td>
                <Badge color={getRoleBadgeColor(user.role)}>{user.role}</Badge>
              </Table.Td>
              <Table.Td>
                {dayjs(user.createdAt).format('MMM DD, YYYY')}
              </Table.Td>
              <Table.Td>
                <Group gap="xs">
                  <ActionIcon
                    variant="light"
                    color="blue"
                    onClick={() => openEditModal(user)}
                  >
                    <IconEdit size={16} />
                  </ActionIcon>
                  <ActionIcon
                    variant="light"
                    color="red"
                    onClick={() => openDeleteModal(user)}
                    disabled={user.id === currentUser?.id}
                  >
                    <IconTrash size={16} />
                  </ActionIcon>
                </Group>
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>

      {/* Create User Modal */}
      <Modal
        opened={createModalOpened}
        onClose={() => setCreateModalOpened(false)}
        title="Create New User"
        size="md"
      >
        <Stack gap="md">
          <TextInput
            label="Full Name"
            placeholder="Enter full name"
            required
            value={formData.fullName}
            onChange={(e) =>
              setFormData({ ...formData, fullName: e.target.value })
            }
          />
          <TextInput
            label="Email"
            placeholder="Enter email"
            type="email"
            required
            value={formData.email}
            onChange={(e) =>
              setFormData({ ...formData, email: e.target.value })
            }
          />
          <PasswordInput
            label="Password"
            placeholder="Enter password"
            required
            value={formData.password}
            onChange={(e) =>
              setFormData({ ...formData, password: e.target.value })
            }
          />
          <Select
            label="Role"
            placeholder="Select role"
            required
            data={ROLES}
            value={formData.role}
            onChange={(value) =>
              setFormData({
                ...formData,
                role: (value as CreateUserDto['role']) || 'PROCUREMENT_OFFICER',
              })
            }
          />
          <Group justify="flex-end" mt="md">
            <Button variant="subtle" onClick={() => setCreateModalOpened(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreate} loading={createUser.isPending}>
              Create
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Edit User Modal */}
      <Modal
        opened={editModalOpened}
        onClose={() => setEditModalOpened(false)}
        title="Edit User"
        size="md"
      >
        <Stack gap="md">
          <TextInput
            label="Full Name"
            placeholder="Enter full name"
            required
            value={formData.fullName}
            onChange={(e) =>
              setFormData({ ...formData, fullName: e.target.value })
            }
          />
          <TextInput
            label="Email"
            placeholder="Enter email"
            type="email"
            required
            value={formData.email}
            onChange={(e) =>
              setFormData({ ...formData, email: e.target.value })
            }
          />
          <PasswordInput
            label="Password (leave blank to keep current password)"
            placeholder="Enter new password"
            value={formData.password}
            onChange={(e) =>
              setFormData({ ...formData, password: e.target.value })
            }
          />
          <Select
            label="Role"
            placeholder="Select role"
            required
            data={ROLES}
            value={formData.role}
            onChange={(value) =>
              setFormData({
                ...formData,
                role: (value as CreateUserDto['role']) || 'PROCUREMENT_OFFICER',
              })
            }
          />
          <Group justify="flex-end" mt="md">
            <Button variant="subtle" onClick={() => setEditModalOpened(false)}>
              Cancel
            </Button>
            <Button onClick={handleEdit} loading={updateUser.isPending}>
              Update
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Delete User Modal */}
      <Modal
        opened={deleteModalOpened}
        onClose={() => setDeleteModalOpened(false)}
        title="Delete User"
        size="md"
      >
        <Stack gap="md">
          <Alert icon={<IconAlertCircle size={16} />} color="red">
            Are you sure you want to delete user <strong>{selectedUser?.fullName}</strong>? This
            action cannot be undone.
          </Alert>
          <Group justify="flex-end" mt="md">
            <Button variant="subtle" onClick={() => setDeleteModalOpened(false)}>
              Cancel
            </Button>
            <Button color="red" onClick={handleDelete} loading={deleteUser.isPending}>
              Delete
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Container>
  );
}

